import React, { FunctionComponent, useRef, useEffect, useState } from "react";
import { ExternalIcon } from "../../components/icons/ExternalIcon";
import "./ScrollHorizontal.css";

interface ScrollHorizontalProps {
  children: React.ReactNode;
  testID?: string;
}

const SCROLL_STEP = 100;

/**
 * Wraps children in a scrollable container with two arrows to scroll left and right that show when there is overflow and ability to scroll.
 * Root child must be inline-flex, width: fit-content, or an explicit width to allow for overflow and scrolling.
 */
export const ScrollHorizontal: FunctionComponent<ScrollHorizontalProps> = ({
  children,
  testID,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const [isScrolledToStart, setIsScrolledToStart] = useState(false);

  const checkOverflow = () => {
    if (containerRef.current) {
      const { scrollWidth, clientWidth } = containerRef.current;
      setHasOverflow(scrollWidth > clientWidth);
    }
  };

  const checkScrollPosition = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      const maxScroll = scrollWidth - clientWidth;
      setIsScrolledToEnd(Math.abs(scrollLeft - maxScroll) < 1);
      setIsScrolledToStart(scrollLeft < 1);
    }
  };

  const scrollRight = () => {
    if (!containerRef.current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
    const maxScroll = scrollWidth - clientWidth;

    if (scrollLeft < maxScroll) {
      containerRef.current.scrollLeft += SCROLL_STEP;
    }
  };

  const scrollLeft = () => {
    if (!containerRef.current) {
      return;
    }

    const { scrollLeft } = containerRef.current;

    if (scrollLeft > 0) {
      containerRef.current.scrollLeft -= SCROLL_STEP;
    }
  };

  useEffect(() => {
    checkOverflow();
    checkScrollPosition();
    window.addEventListener("resize", checkOverflow);

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
    }

    return () => {
      window.removeEventListener("resize", checkOverflow);
      if (container) {
        container.removeEventListener("scroll", checkScrollPosition);
      }
    };
  }, [children, containerRef]);

  const showRightArrow = hasOverflow && !isScrolledToEnd;
  const showLeftArrow = hasOverflow && !isScrolledToStart;

  return (
    <div data-testid={testID} className="scroll-horizontal">
      <div ref={containerRef} className="scroll-horizontal__content">
        {children}
      </div>
      {showLeftArrow && (
        <>
          <div className="scroll-horizontal__arrow-background scroll-horizontal__arrow-left"></div>
          <div
            className="scroll-horizontal__arrow scroll-horizontal__arrow-left"
            onClick={scrollLeft}
            role="button"
            tabIndex={0}
            aria-label="Scroll left">
            <ExternalIcon svgName="arrow-left" size={20} />
          </div>
        </>
      )}
      {showRightArrow && (
        <>
          <div className="scroll-horizontal__arrow-background scroll-horizontal__arrow-right"></div>
          <div
            className="scroll-horizontal__arrow scroll-horizontal__arrow-right"
            onClick={scrollRight}
            role="button"
            tabIndex={0}
            aria-label="Scroll right">
            <ExternalIcon svgName="arrow-right" size={20} />
          </div>
        </>
      )}
    </div>
  );
};
